
import {defineComponent} from "vue";
import {Exception} from "@/api/interfaces";
import {CodeDescriptionRestDto} from "@/api/pharma-cms-content-block/models";
import {ContentBlockInRowRestDtoModel} from "@/models/ContentBlockInRowRestDtoModel";
import {ContentBlockInRowCreateRestDtoModel} from "@/models/ContentBlockInRowCreateRestDtoModel";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import ButtonCbLibraries from "@/components/layouts/content-pages/wysiwyg/libraries/ButtonCbLibraries.vue";
import {ContentBlockTypesEnum} from "@/helpers/functions/enum";
import {DpException} from "@/exception/DpException";
import ContentBlockTypesWithLibrary from "@/models/rules/ContentBlockTypesWithLibrary";
import {ImageContentRestDtoModel} from "@/models/api/pharma-cms-content-block/ImageContentRestDtoModel";
import {TextWithImageContentRestDtoModel} from "@/models/api/pharma-cms-content-block/TextWithImageContentRestDtoModel";
import {TextWithTitleContentRestDtoModel} from "@/models/api/pharma-cms-content-block/TextWithTitleContentRestDtoModel";
import InputSelect from "@/components/UI/InputSelect.vue";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import {ContentBlockEnumRestService} from "@/services/rest/cms-content-block/ContentBlockEnumRestService";
import {NormalContentBlockRestService} from "@/services/rest/cms-content-block/NormalContentBlockRestService";
import {ContentBlockLinkRestService} from "@/services/rest/cms-content-block/ContentBlockLinkRestService";
import {ContentBlockInRowRestService} from "@/services/rest/cms-content-page/ContentBlockInRowRestService";

export default defineComponent({
  name: "ContentPageAddColumn",
  components: {InputSelect, ButtonCbLibraries},
  emits: ['submit'],
  props: {
    rowId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isReady: false as boolean,

      cbInRow: ContentBlockInRowRestDtoModel.createWithDefaults(),
      exceptions: [] as Exception[],

      contentBlockStructureTypeEnums: null as CodeDescriptionRestDto[] | null,
      contentBlockTypesEnum: ContentBlockTypesEnum as unknown as ContentBlockTypesEnum[],
      columnWidthCodeEnum: [] as CodeDescriptionRestDto[],
    };
  },
  computed: {
    cbType(): ContentBlockTypesEnum {
      const structureCode = this.cbInRow.structure_code as any;
      return this.contentBlockTypesEnum[structureCode];
    },
    contentBlockStructureDescription(): string {
      return this.contentBlockStructureTypeEnums
        ?.find((codeDescription: CodeDescriptionRestDto) => codeDescription.code === this.cbInRow?.structure_code)
        ?.description ?? '';
    }
  },
  mounted() {
    this.initialize();
  },
  methods: {
    /**
     * Called by the page every time the offcanvas is opened, so a previous selection is not still
     * standing there. Was the 'openAddColumn' bus channel.
     */
    reset(): void {
      this.cbInRow = ContentBlockInRowRestDtoModel.createWithDefaults();
      this.exceptions = [];
    },
    async initialize(): Promise<void> {
      this.isReady = false;

      try {
        [this.contentBlockStructureTypeEnums, this.columnWidthCodeEnum] = await Promise.all([
          ContentBlockEnumRestService.getInstance().findContentBlockStructureTypeEnums(true),
          EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.COLUMN_SIZE, DpModuleEnum.CMS),
        ]);
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
      }

      this.isReady = true;
    },
    async saveColumn(): Promise<void> {
      this.exceptions = [];

      try {
        await ContentBlockInRowRestService.getInstance()
          .createContentBlockInRow(new ContentBlockInRowCreateRestDtoModel(null, this.cbInRow), this.rowId);

        this.$emit('submit');
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
      }
    },
    async saveColumnFromLib(libCbId: string): Promise<void> {
      this.exceptions = [];

      try {
        if (!ContentBlockTypesWithLibrary.includes(this.cbType)) {
          throw [new DpException('CB type library not supported!')];
        }

        // Create the content block, link it to the chosen library block, then create the column
        // pointing at it.
        let cbId;
        switch (this.cbType) {
          case ContentBlockTypesEnum.IMAGE:
            cbId = await NormalContentBlockRestService.getInstance()
              .createImage(ImageContentRestDtoModel.createWithDefaults(), this.cbType);
            break;
          case ContentBlockTypesEnum.TEXT_WITH_IMAGE:
            cbId = await NormalContentBlockRestService.getInstance()
              .createTextWithImage(TextWithImageContentRestDtoModel.createWithDefaults(), this.cbType);
            break;
          case ContentBlockTypesEnum.TEXT_WITH_TITLE:
            cbId = await NormalContentBlockRestService.getInstance()
              .createTextWithTitle(TextWithTitleContentRestDtoModel.createWithDefaults(), this.cbType);
            break;
          default:
            throw [new DpException('Variable cbId not initialised!')];
        }
        if (!cbId) {
          throw [new DpException('No cbId received from create!')];
        }

        await ContentBlockLinkRestService.getInstance()
          .createContentBlockLink(libCbId, this.cbType, cbId, false);

        this.cbInRow.cb_id = cbId;

        await this.saveColumn();
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
      }
    },
    submitData(): void {
      this.saveColumn();
    },
    setExceptions(exceptions: Exception[]): void {
      this.exceptions = exceptions;
    }
  },
});
